import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Shield,
  Lock,
  Users,
  Clock,
  UserCheck,
  Globe,
  FileText,
  ChevronRight,
  ChevronUp,
  Download,
  Printer,
  Search,
  X,
  Mail,
  ArrowRight,
  Eye,
  AlertCircle,
  ChevronDown,
} from "lucide-react";
import {
  policyMetadata,
  policySections,
  policyTableOfContents,
  privacySummary,
} from "../data/privacyPolicyData";

const iconMap: Record<string, any> = {
  Shield,
  Lock,
  Users,
  Clock,
  UserCheck,
  Globe,
};

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(policySections.map((s) => s.id))
  );
  const location = useLocation();

  // Handle scroll to section from URL hash
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [location]);

  // Track active section and show/hide back to top button
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);

      const sections = policySections.map((s) => document.getElementById(s.id));
      const scrollPos = window.scrollY + 150;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPos) {
          setActiveSection(policySections[i].id);
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const toggleSection = (id: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const filteredSections = searchQuery
    ? policySections.filter(
        (section) =>
          section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          section.content.some((c) =>
            c.toLowerCase().includes(searchQuery.toLowerCase())
          ) ||
          section.subsections?.some(
            (sub) =>
              sub.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
              sub.content.some((c) =>
                c.toLowerCase().includes(searchQuery.toLowerCase())
              )
          )
      )
    : policySections;

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-[#FAFAFA]" />

        {/* Subtle Grid Pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%238B52FF' fill-opacity='1'%3E%3Ccircle cx='30' cy='30' r='1.5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        {/* Animated Gradient Orbs */}
        <motion.div
          className="absolute top-20 left-[10%] w-[500px] h-[500px] rounded-full blur-3xl"
          style={{
            background:
              "radial-gradient(circle, rgba(139, 82, 255, 0.15) 0%, transparent 70%)",
          }}
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute bottom-20 right-[10%] w-[600px] h-[600px] rounded-full blur-3xl"
          style={{
            background:
              "radial-gradient(circle, rgba(224, 90, 106, 0.12) 0%, transparent 70%)",
          }}
          animate={{
            scale: [1, 1.15, 1],
            opacity: [0.3, 0.4, 0.3],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
        />

        {/* Decorative Elements */}
        <div className="absolute top-32 right-[15%] w-3 h-3 rounded-full bg-[#8B52FF]/30" />
        <div className="absolute top-48 right-[25%] w-2 h-2 rounded-full bg-[#E05A6A]/30" />
        <div className="absolute bottom-32 left-[20%] w-4 h-4 rounded-full bg-[#8B52FF]/20" />
        <div className="absolute bottom-48 left-[10%] w-2 h-2 rounded-full bg-[#E05A6A]/20" />

        {/* Floating Cards */}
        <motion.div
          className="absolute top-24 right-[8%] hidden lg:block"
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        >
          <div className="bg-white rounded-2xl shadow-xl p-4 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B52FF]/20 to-[#E05A6A]/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-[#8B52FF]" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Protected</p>
                <p className="text-sm font-semibold text-gray-900">
                  256-bit SSL
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="absolute bottom-32 left-[5%] hidden lg:block"
          animate={{ y: [0, 10, 0] }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
        >
          <div className="bg-white rounded-2xl shadow-xl p-4 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B52FF]/20 to-[#E05A6A]/20 flex items-center justify-center">
                <Lock className="w-5 h-5 text-[#E05A6A]" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Compliance</p>
                <p className="text-sm font-semibold text-gray-900">
                  GDPR Ready
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-lg border border-gray-100 mb-8"
          >
            <div className="w-2 h-2 rounded-full bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] animate-pulse" />
            <span className="text-sm font-medium text-gray-700">
              Your Privacy Matters
            </span>
            <Shield className="w-4 h-4 text-[#8B52FF]" />
          </motion.div>

          {/* Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight mb-6"
          >
            Privacy{" "}
            <span className="relative inline-block">
              <span className="bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] bg-clip-text text-transparent">
                Policy
              </span>
              {/* Decorative underline */}
              <motion.svg
                className="absolute -bottom-2 left-0 w-full"
                viewBox="0 0 200 12"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 1, delay: 0.5 }}
              >
                <motion.path
                  d="M2 8 Q50 2, 100 8 T198 8"
                  fill="none"
                  stroke="url(#privacyGradient)"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                <defs>
                  <linearGradient
                    id="privacyGradient"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop offset="0%" stopColor="#8B52FF" />
                    <stop offset="100%" stopColor="#E05A6A" />
                  </linearGradient>
                </defs>
              </motion.svg>
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg md:text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed"
          >
            We are committed to protecting your privacy and being transparent
            about how we collect, use, and safeguard your personal information.
          </motion.p>

          {/* Meta Info Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-wrap items-center justify-center gap-4 mb-10"
          >
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white shadow-md border border-gray-100">
              <Clock className="w-4 h-4 text-[#8B52FF]" />
              <span className="text-sm text-gray-600">
                Effective:{" "}
                <span className="font-medium text-gray-900">
                  {policyMetadata.effectiveDate}
                </span>
              </span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white shadow-md border border-gray-100">
              <FileText className="w-4 h-4 text-[#8B52FF]" />
              <span className="text-sm text-gray-600">
                Version{" "}
                <span className="font-medium text-gray-900">
                  {policyMetadata.version}
                </span>
              </span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white shadow-md border border-gray-100">
              <Eye className="w-4 h-4 text-[#E05A6A]" />
              <span className="text-sm text-gray-600">
                Updated:{" "}
                <span className="font-medium text-gray-900">
                  {policyMetadata.lastUpdated}
                </span>
              </span>
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <button
              onClick={handlePrint}
              className="group relative inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-medium shadow-lg hover:shadow-xl hover:shadow-[#8B52FF]/25 transition-all duration-300 overflow-hidden"
            >
              {/* Shimmer effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              <Printer className="w-5 h-5 relative z-10" />
              <span className="relative z-10">Print Document</span>
            </button>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="mt-16"
          >
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="flex flex-col items-center gap-2 text-gray-400"
            >
              <span className="text-xs font-medium uppercase tracking-wider">
                Scroll to read
              </span>
              <ChevronDown className="w-5 h-5" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Privacy at a Glance */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {privacySummary.title}
            </h2>
            <p className="text-gray-600">
              A quick overview of our privacy practices
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {privacySummary.items.map((item, index) => {
              const Icon = iconMap[item.icon];
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="p-6 rounded-2xl border border-gray-100 bg-gradient-to-br from-gray-50 to-white hover:shadow-lg transition-shadow"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#8B52FF]/10 to-[#E05A6A]/10 flex items-center justify-center mb-4">
                    {Icon && <Icon className="w-6 h-6 text-[#8B52FF]" />}
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {item.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Data Controller Notice */}
      <section className="bg-blue-50 border-b border-blue-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-blue-800">
                <strong>Data Controller:</strong>{" "}
                {policyMetadata.dataController}, located at 123 Innovation
                Drive, Suite 500, San Francisco, CA 94105, USA. For privacy
                inquiries, contact{" "}
                <a
                  href="mailto:privacy@unimerg.com"
                  className="text-[#8B52FF] hover:underline font-medium"
                >
                  privacy@unimerg.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar - Table of Contents */}
            <aside className="lg:w-72 flex-shrink-0">
              <div className="lg:sticky lg:top-24 space-y-6">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search policy..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF] transition-all text-sm"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-100 rounded transition-colors"
                    >
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  )}
                </div>

                {/* Table of Contents */}
                <div className="bg-white rounded-2xl border border-gray-100 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 px-2">
                    Table of Contents
                  </h3>
                  <nav className="space-y-1 max-h-[60vh] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                    {policyTableOfContents.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-all ${
                          activeSection === item.id
                            ? "bg-gradient-to-r from-[#8B52FF]/10 to-[#E05A6A]/10 text-[#8B52FF] font-medium"
                            : "text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        <ChevronRight
                          className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${
                            activeSection === item.id ? "text-[#8B52FF]" : ""
                          }`}
                        />
                        <span className="line-clamp-1">{item.title}</span>
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Quick Actions */}
                <div className="bg-gradient-to-br from-[#8B52FF] to-[#E05A6A] rounded-2xl p-5 text-white">
                  <Shield className="w-8 h-8 mb-3 opacity-80" />
                  <h3 className="font-semibold mb-2">Exercise Your Rights</h3>
                  <p className="text-sm text-white/80 mb-4">
                    Request access, correction, or deletion of your data.
                  </p>
                  <Link
                    to="/help"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-sm font-medium transition-colors"
                  >
                    Submit Request
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                {/* Related Documents */}
                <div className="bg-white rounded-2xl border border-gray-100 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">
                    Related Documents
                  </h3>
                  <div className="space-y-2">
                    <Link
                      to="/terms"
                      className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#8B52FF] transition-colors"
                    >
                      <FileText className="w-4 h-4" />
                      Terms of Service
                    </Link>
                    {/* <Link
                      to="/cookies"
                      className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#8B52FF] transition-colors"
                    >
                      <FileText className="w-4 h-4" />
                      Cookie Policy
                    </Link>
                    <Link
                      to="/acceptable-use"
                      className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#8B52FF] transition-colors"
                    >
                      <FileText className="w-4 h-4" />
                      Acceptable Use Policy
                    </Link> */}
                  </div>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 min-w-0">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
                <div className="p-6 md:p-10 prose prose-gray max-w-none">
                  {/* Search Results Info */}
                  {searchQuery && (
                    <div className="mb-8 p-4 rounded-xl bg-[#8B52FF]/5 border border-[#8B52FF]/20">
                      <p className="text-sm text-gray-700">
                        Found{" "}
                        <strong className="text-[#8B52FF]">
                          {filteredSections.length}
                        </strong>{" "}
                        section{filteredSections.length !== 1 ? "s" : ""}{" "}
                        matching "{searchQuery}"
                      </p>
                    </div>
                  )}

                  {/* Sections */}
                  {filteredSections.map((section) => (
                    <section
                      key={section.id}
                      id={section.id}
                      className="scroll-mt-24 mb-12 last:mb-0"
                    >
                      {/* Section Header */}
                      <button
                        onClick={() => toggleSection(section.id)}
                        className="w-full flex items-center justify-between group mb-4"
                      >
                        <h2 className="text-xl md:text-2xl font-bold text-gray-900 group-hover:text-[#8B52FF] transition-colors text-left">
                          {section.title}
                        </h2>
                        <ChevronRight
                          className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${
                            expandedSections.has(section.id) ? "rotate-90" : ""
                          }`}
                        />
                      </button>

                      {expandedSections.has(section.id) && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          transition={{ duration: 0.2 }}
                        >
                          {/* Section Content */}
                          {section.content.map((paragraph, idx) => (
                            <p
                              key={idx}
                              className="text-gray-600 leading-relaxed mb-4"
                            >
                              {paragraph}
                            </p>
                          ))}

                          {/* Subsections */}
                          {section.subsections?.map((subsection, subIdx) => (
                            <div key={subIdx} className="mt-6 ml-4">
                              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                                {subsection.title}
                              </h3>
                              {subsection.content.map((paragraph, pIdx) => (
                                <p
                                  key={pIdx}
                                  className="text-gray-600 leading-relaxed mb-3"
                                >
                                  {paragraph}
                                </p>
                              ))}
                            </div>
                          ))}

                          {/* Section Divider */}
                          <div className="mt-8 pt-8 border-t border-gray-100" />
                        </motion.div>
                      )}
                    </section>
                  ))}

                  {/* Contact Section Highlight */}
                  <section className="mt-12 p-6 rounded-2xl bg-gradient-to-br from-[#8B52FF]/5 to-[#E05A6A]/5 border border-[#8B52FF]/20">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#8B52FF] to-[#E05A6A] flex items-center justify-center">
                        <Mail className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          Questions About Your Privacy?
                        </h3>
                        <p className="text-gray-600 text-sm leading-relaxed mb-4">
                          If you have any questions about this Privacy Policy or
                          how we handle your personal information, please don't
                          hesitate to contact us. We're here to help.
                        </p>
                        <div className="flex flex-wrap gap-4">
                          <a
                            href="mailto:privacy@unimerg.com"
                            className="inline-flex items-center gap-2 text-[#8B52FF] hover:text-[#E05A6A] font-medium text-sm transition-colors"
                          >
                            <Mail className="w-4 h-4" />
                            privacy@unimerg.com
                          </a>
                          <a
                            href="mailto:dpo@unimerg.com"
                            className="inline-flex items-center gap-2 text-[#8B52FF] hover:text-[#E05A6A] font-medium text-sm transition-colors"
                          >
                            <Shield className="w-4 h-4" />
                            dpo@unimerg.com (Data Protection Officer)
                          </a>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Last Updated */}
                  <div className="mt-8 pt-8 border-t border-gray-100 text-center">
                    <p className="text-sm text-gray-500">
                      This Privacy Policy was last updated on{" "}
                      <strong>{policyMetadata.lastUpdated}</strong>
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Version {policyMetadata.version} • Data Controller:{" "}
                      {policyMetadata.dataController}
                    </p>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </section>

      {/* Rights CTA Section */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Your Privacy Rights
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Depending on your location, you may have certain rights regarding
              your personal information. We make it easy to exercise these
              rights.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                icon: Eye,
                title: "Access",
                description: "Request a copy of your data",
              },
              {
                icon: FileText,
                title: "Correct",
                description: "Update inaccurate information",
              },
              {
                icon: X,
                title: "Delete",
                description: "Request data deletion",
              },
              {
                icon: Download,
                title: "Export",
                description: "Download your data",
              },
            ].map((right, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-5 rounded-xl border border-gray-100 bg-gray-50 hover:bg-white hover:shadow-md transition-all text-center"
              >
                <div className="w-10 h-10 mx-auto rounded-lg bg-gradient-to-br from-[#8B52FF]/10 to-[#E05A6A]/10 flex items-center justify-center mb-3">
                  <right.icon className="w-5 h-5 text-[#8B52FF]" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  {right.title}
                </h3>
                <p className="text-sm text-gray-500">{right.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/help"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold hover:shadow-lg hover:shadow-[#8B52FF]/25 transition-all"
            >
              Submit a Privacy Request
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Have questions about our Privacy Policy?
          </h2>
          <p className="text-gray-300 mb-8">
            Our privacy team is here to help answer any questions you may have.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="mailto:privacy@unimerg.com"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-gray-900 font-semibold hover:bg-gray-100 transition-all"
            >
              <Mail className="w-4 h-4" />
              Contact Privacy Team
            </a>
            <Link
              to="/faqs"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-white/20 text-white font-semibold hover:bg-white/10 transition-all"
            >
              View Privacy FAQs
            </Link>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      {showBackToTop && (
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 p-3 rounded-full bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white shadow-lg hover:shadow-xl transition-all"
        >
          <ChevronUp className="w-5 h-5" />
        </motion.button>
      )}
    </div>
  );
};

export default PrivacyPolicy;
